// src/pages/Resources.js
import React, { useState, useEffect } from 'react';
import { ClipboardList, MapPin, Mail, HelpCircle, DollarSign, Vote, Calendar, ExternalLink, BookOpen, Loader, Bell, CheckCircle, AlertTriangle } from 'lucide-react';
import { fetchResources, upsertSubscriberToken } from '../utils/airtable';
import { requestNotificationPermission } from '../firebase';
import { EXTERNAL_LINKS } from '../utils/theme';
import './Resources.css';

const ICON_COLOR = '#182B45';

// Maps a resource's Title to a Lucide icon component. Matches by keyword so
// this works for both the fallback resources below and live Airtable rows.
const getResourceIcon = (title = '') => {
  const t = title.toLowerCase();
  if (t.includes('register')) return ClipboardList;
  if (t.includes('drop box') || t.includes('voting center')) return MapPin;
  if (t.includes('mail ballot')) return Mail;
  if (t.includes('faq')) return HelpCircle;
  if (t.includes('tracer') || t.includes('campaign finance')) return DollarSign;
  if (t.includes('candidate list')) return Vote;
  if (t.includes('election calendar')) return Calendar;
  return ExternalLink;
};

const isPartyOrgResource = (title = '') => title.toLowerCase().includes('republican party');

// Fallback resources if Airtable is empty or not connected
const getFallbackResources = (county) => {
  const countyName = county?.name || 'Larimer County';
  const resources = [
    { Title: 'Register to vote', Description: 'Check or update your registration via CO SOS', URL: EXTERNAL_LINKS.coSOS },
    { Title: 'Drop boxes & voting centers', Description: `Find ${countyName} locations and hours`, URL: county?.whereToVoteUrl || EXTERNAL_LINKS.larimerClerk },
    { Title: 'Mail ballot info', Description: 'Track, return, or request a replacement ballot', URL: EXTERNAL_LINKS.mailBallot },
    { Title: 'Voter FAQ', Description: 'Common questions about voting in Colorado', URL: EXTERNAL_LINKS.voterFAQ },
    { Title: 'TRACER campaign finance', Description: 'Search candidate campaign finance filings', URL: EXTERNAL_LINKS.tracer },
    { Title: 'Official candidate list', Description: '2026 primary election candidates via CO SOS', URL: EXTERNAL_LINKS.candidateList },
    { Title: '2026 Election Calendar', Description: 'Key dates and deadlines for the 2026 election cycle from the CO Secretary of State', URL: 'https://www.sos.state.co.us/pubs/elections/calendars/2026ElectionCalendar.pdf' },
  ];
  if (county?.orgWebsite) {
    resources.push({ Title: county.orgName, Description: `Official website of the ${county.orgName}`, URL: county.orgWebsite });
  }
  return resources;
};

const ResourceCard = ({ resource, county }) => {
  const title = resource.Title || '';
  const Icon = getResourceIcon(title);

  return (
    <a className="res-card" href={resource.URL} target="_blank" rel="noreferrer">
      <div className="res-icon">
        {isPartyOrgResource(title)
          ? <img src={county?.orgLogo || '/logo.png'} alt="County GOP" style={{ width: 80, height: 80, objectFit: 'contain', borderRadius: 4 }} />
          : <Icon size={20} color={ICON_COLOR} />
        }
      </div>
      <div className="res-title">{resource.Title}</div>
      <div className="res-desc">{resource.Description}</div>
    </a>
  );
};

// Re-registers push notifications for a user whose token was deleted or
// expired — same requestNotificationPermission + upsertSubscriberToken flow
// as NotificationPrompt, but user-initiated instead of the automatic prompt.
// upsertSubscriberToken reactivates the existing record for this token
// instead of creating a duplicate if one already exists.
const EnableNotificationsButton = () => {
  const [status, setStatus] = useState('idle'); // 'idle', 'loading', 'success', 'error'

  const handleClick = async () => {
    setStatus('loading');
    try {
      const token = await requestNotificationPermission();
      if (!token) {
        setStatus('error');
        return;
      }
      await upsertSubscriberToken(token);
      setStatus('success');
    } catch (err) {
      console.log('Could not re-enable notifications:', err);
      setStatus('error');
    }
  };

  return (
    <div style={{ marginTop: 20 }}>
      <button className="btn-red" onClick={handleClick} disabled={status === 'loading'}>
        {status === 'loading'
          ? <><Loader size={16} color="currentColor" style={{ verticalAlign: 'middle' }} /> Enabling...</>
          : <><Bell size={16} color="currentColor" style={{ verticalAlign: 'middle' }} /> Enable notifications</>}
      </button>
      {status === 'success' && (
        <div className="success-msg" style={{ marginTop: 10 }}>
          <CheckCircle size={16} color="currentColor" style={{ verticalAlign: 'middle' }} /> Notifications enabled!
        </div>
      )}
      {status === 'error' && (
        <div className="error-msg" style={{ marginTop: 10 }}>
          <AlertTriangle size={16} color="currentColor" style={{ verticalAlign: 'middle' }} /> Could not enable notifications. Check your browser's notification permissions and try again.
        </div>
      )}
    </div>
  );
};

const Resources = ({ county }) => {
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fallback = getFallbackResources(county);
    fetchResources()
      .then(data => {
        setResources(data.length > 0 ? data : fallback);
        setLoading(false);
      })
      .catch(() => {
        setResources(fallback);
        setLoading(false);
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div>
      <div className="sec-head">
        <div className="sec-icon"><BookOpen size={20} color="currentColor" style={{ verticalAlign: 'middle' }} /></div>
        <div>
          <div className="sec-title">Voter resources</div>
          <div className="sec-desc">Everything you need to register, vote, and stay informed</div>
        </div>
      </div>

      {loading && <div className="loading"><Loader size={16} color="currentColor" style={{ verticalAlign: 'middle' }} /> Loading resources...</div>}

      {!loading && (
        <div className="res-grid">
          {resources.map((r, i) => <ResourceCard key={r.id || i} resource={r} county={county} />)}
        </div>
      )}

      <EnableNotificationsButton />
    </div>
  );
};

export default Resources;
